import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { PersonService } from "../../../../services/person.service";
import { ToastrService } from "ngx-toastr";
import { Person } from "../../../../models/person";
import { Responsibility } from "../../../responsibilities/responsibility.model";
import { ProfessionalsService } from "../../services/professionals.service";
import { ContractType, PersonType, ProfessionalRequest } from "../../professional.model";
import { ResponsibilitiesService } from "src/app/features/responsibilities/services/responsibilities.service";
import { ErrorHandlerService } from "src/app/shared/error-handler.service";
import { OfficeService } from "@app/services/office.service";

@Component({
  selector: "app-professionals-form",
  templateUrl: "./professionals-form.component.html",
  styleUrls: ["./professionals-form.component.css"],
})
export class ProfessionalsFormComponent implements OnInit {
  isLoading: boolean = false;
  isSaving = false;
  isLoadingOffices = false;
  isLoadingResponsibilities = false;

  imageUrl: string | ArrayBuffer | null = null;
  selectedFile: File | null = null;

  formGroup: FormGroup;

  offices: any[] = [];
  responsibilities: Responsibility[] = [];
  supervisors: Person[] = [];
  managers: Person[] = [];
  roles: string[] = [];

  get id() {
    return this.formGroup.get("id");
  }
  get name() {
    return this.formGroup.get("name");
  }
  get email() {
    return this.formGroup.get("email");
  }
  get username() {
    return this.formGroup.get("username");
  }
  get password() {
    return this.formGroup.get("password");
  }
  get confirmPassword() {
    return this.formGroup.get("confirmPassword");
  }
  get officeId() {
    return this.formGroup.get("officeId");
  }
  get responsibilityId() {
    return this.formGroup.get("responsibilityId");
  }
  get accessType() {
    return this.formGroup.get("accessType");
  }
  get supervisorId() {
    return this.formGroup.get("supervisorId");
  }
  get managerId() {
    return this.formGroup.get("managerId");
  }
  get picture() {
    return this.formGroup.get("picture");
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private personService: PersonService,
    private officeService: OfficeService,
    private responsibilitiesService: ResponsibilitiesService,
    private route: ActivatedRoute,
    private toast: ToastrService,
    private errorHandler: ErrorHandlerService,
    private professionalService: ProfessionalsService,
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.getOffices();
    this.getManagers();
    this.getSupervisors();
    this.loadInitialData();
  }

  initializeForm(): void {
    this.formGroup = this.fb.group({
      id: [""],
      username: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      password: ["", [Validators.minLength(6)]],
      confirmPassword: ["", [Validators.minLength(6)]],
      name: ["", Validators.required],
      picture: [null],
      contractType: [""],
      personType: [""],
      office: [],
      responsibility: [],
      officeId: ["", Validators.required],
      responsibilityId: ["", Validators.required],
      managerId: [""],
      supervisorId: [""],
      accessType: ["", Validators.required],
    });
  }

  getOffices() {
    this.officeService.findAll().subscribe({
      next: (response: any[]) => {
        this.offices = response;
      },
      error: () => {
        this.toast.error("Erro ao carregar empresas. Tente novamente mais tarde.");
      },
    });
  }

  getManagers(): void {
    this.personService.findAllManagers().subscribe({
      next: (response: any[]) => (this.managers = response),
      error: () => this.toast.error("Erro ao carregar gestores. Tente novamente mais tarde."),
    });
  }

  getSupervisors(): void {
    this.personService.findAllSupervisors().subscribe({
      next: (response: any[]) => (this.supervisors = response),
      error: () => this.toast.error("Erro ao carregar supervisores. Tente novamente mais tarde."),
    });
  }

  loadInitialData(): void {
    const id = this.route.snapshot.params["id"];
    if (id) {
      this.personService.findById(id).subscribe({
        next: (response) => {
          this.formGroup.get("id").patchValue(id);
          this.formGroup.get("name").patchValue(response.name);
          this.formGroup.get("email").patchValue(response.email);
          this.formGroup.get("contractType").patchValue(response.contractType);
          this.formGroup.get("personType").patchValue(response.personType);
          this.formGroup.get("username").patchValue(response.username);
          this.formGroup.get("officeId").patchValue(response.officeId);
          this._loadResponsibilitiesData(response.officeId);
          this.formGroup.get("responsibilityId").patchValue(response.responsibilityId);
          this.formGroup.get("accessType").patchValue(response.accessType);
          this.formGroup.get("managerId").patchValue(response.managerId);
          this.formGroup.get("supervisorId").patchValue(response.supervisorId);
          this.imageUrl = response.picture;
        },
        error: () => this.toast.error("Erro ao carregar funcionário para edição. Tente novamente mais tarde."),
      });
    }
  }

  onSubmit() {
    this.isSaving = true;

    if (this.formGroup.valid) {
      if (this.formGroup.get("password").value !== this.formGroup.get("confirmPassword").value) {
        this.toast.error("As senhas não coincidem. Por favor, verifique e tente novamente.");
        this.isSaving = false;
        return;
      }

      const professional = this.buildProfessionalRequest();

      if (this.formGroup.get("id").value != "") {
        // update
        this.professionalService.update(this.formGroup.get("id").value, professional, this.selectedFile).subscribe({
          next: () => {
            this.router.navigate(["/profissionais-autonomos"]).then((success) => {
              if (success) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            });
            this.isSaving = false;
            this.toast.success("🎉 Profissional alterado com sucesso!");
          },
          error: (error: Error) => {
            this.isSaving = false;
            this.toast.error("Erro ao atualizar profissional. Tente novamente mais tarde.");
          },
          complete: () => {
            this.formGroup.reset();
          },
        });
      } else {
        // create
        this.professionalService.create(professional, this.selectedFile).subscribe({
          next: () => {
            this.router.navigate(["/profissionais-autonomos"]).then((success) => {
              if (success) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            });
            this.isSaving = false;
            this.toast.success("🎉 Profissional salvo com sucesso!");
          },
          error: (error: Error) => {
            this.isSaving = false;
            this.toast.error("Erro ao salvar profissional. Tente novamente mais tarde.");
          },
          complete: () => {
            this.formGroup.reset();
          },
        });
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
      this.toast.error("O formulário para cadastro não é válido. Alguma informação deve estar errada.");
    }
  }

  buildProfessionalRequest(): ProfessionalRequest {
    switch (this.formGroup.get("accessType")?.value) {
      case "Manager":
        this.formGroup.get("personType").patchValue(PersonType.MANAGER);
        break;
      case "Supervisor":
        this.formGroup.get("personType").patchValue(PersonType.SUPERVISOR);
        break;
      case "User":
        this.formGroup.get("personType").patchValue(PersonType.USER);
        break;
      default:
        this.formGroup.get("personType").patchValue(PersonType.USER);
    }

    return {
      name: this.formGroup.get("name")?.value,
      cpfCnpj: "",
      officeId: this.formGroup.get("officeId")?.value,
      responsibilityId: this.formGroup.get("responsibilityId")?.value,
      managerId: this.formGroup.get("managerId")?.value,
      supervisorId: this.formGroup.get("supervisorId")?.value,
      accessType: this.formGroup.get("accessType")?.value,
      personType: this.formGroup.get("personType")?.value,
      contractType: ContractType.PROFESSIONAL,
      username: this.formGroup.get("username")?.value,
      email: this.formGroup.get("email")?.value,
      password: this.formGroup.get("password")?.value,
      roles: this.roles,
    };
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];

    if (file) {
      this.selectedFile = file;
      this.formGroup.patchValue({
        photo: file,
      });

      const reader = new FileReader();
      reader.onload = (e) => {
        this.imageUrl = e.target?.result;
      };
      reader.readAsDataURL(file);
    }
  }

  private _loadResponsibilitiesData(officeId: string): void {
    this.responsibilitiesService.findByOffice(officeId).subscribe({
      next: (response: any[]) => (this.responsibilities = response),
      error: (error) => this.errorHandler.handle(error, "Erro ao carregar cargos."),
    });
  }

  onOfficeChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("officeId").patchValue(value);

    // Limpar o cargo quando a empresa mudar
    this.formGroup.get("responsibilityId").patchValue("");

    // Carregar responsabilidades da nova empresa
    if (value) {
      this._loadResponsibilitiesData(value);
    } else {
      // Se não houver empresa selecionada, limpar a lista de responsabilidades
      this.responsibilities = [];
    }
  }

  onResponsibilityChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("responsibilityId").patchValue(value);
  }

  onSupervisorChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("supervisorId").patchValue(value);
  }

  onManagerChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("managerId").patchValue(value);
  }

  onInputChange(): void {
    const usernameControl = this.formGroup.get("username");
    usernameControl?.setValue(usernameControl.value.replace(/\s/g, ""));
  }
}
